import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Edit, Loader2, Lock, MessageSquare, Plus, Save, Trash2, Unlock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Cohort } from "@/lib/api/sms-lifecycle";
import { useCurrentUser } from "@/lib/api/user";
import {
  useCreateCohort,
  useDeleteCohort,
  useReorderCohorts,
  useCohorts,
  useUnlockCohort,
  useUpdateCohort,
} from "@/lib/hooks/sms-lifecycle/use-sms-lifecycle";

interface CohortsTabProps {
  onSelectCohort: (id: string) => void;
}

export const CohortsTab = ({ onSelectCohort }: CohortsTabProps) => {
  const { data: currentUser } = useCurrentUser();
  const isDeveloper = currentUser?.metadata?.developer === true;

  const { data: cohorts, isLoading } = useCohorts();
  const createMutation = useCreateCohort();
  const updateMutation = useUpdateCohort();
  const deleteMutation = useDeleteCohort();
  const reorderMutation = useReorderCohorts();
  const unlockMutation = useUnlockCohort();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCohortName, setNewCohortName] = useState("");
  const [newCohortDesc, setNewCohortDesc] = useState("");

  const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editSql, setEditSql] = useState("");
  const [editTwoWaySms, setEditTwoWaySms] = useState(false);

  const [deletingCohortId, setDeletingCohortId] = useState<string | null>(null);

  const handleCreate = async () => {
    await createMutation.mutateAsync({ name: newCohortName, description: newCohortDesc });
    setShowCreateDialog(false);
    setNewCohortName("");
    setNewCohortDesc("");
  };

  const handleToggleEnabled = async (cohort: Cohort) => {
    await updateMutation.mutateAsync({ id: cohort.id, enabled: !cohort.enabled });
  };

  const handleDelete = (id: string) => {
    setDeletingCohortId(id);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCohortId) return;
    await deleteMutation.mutateAsync(deletingCohortId);
    setDeletingCohortId(null);
  };

  const handleMoveUp = (index: number) => {
    if (!cohorts || index === 0) return;
    const newOrder = [...cohorts];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderMutation.mutate(newOrder.map((c) => c.id));
  };

  const handleMoveDown = (index: number) => {
    if (!cohorts || index === cohorts.length - 1) return;
    const newOrder = [...cohorts];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderMutation.mutate(newOrder.map((c) => c.id));
  };

  const openEditDialog = (cohort: Cohort) => {
    setEditingCohort(cohort);
    setEditName(cohort.name);
    setEditDesc(cohort.description);
    setEditSql(cohort.sqlStatement);
    setEditTwoWaySms(cohort.twoWaySms);
  };

  const handleSaveEdit = async () => {
    if (!editingCohort) return;
    await updateMutation.mutateAsync({
      id: editingCohort.id,
      name: editName,
      description: editDesc,
      twoWaySms: editTwoWaySms,
      ...(isDeveloper ? { sqlStatement: editSql } : {}),
    });
    setEditingCohort(null);
  };

  if (isLoading) return (
    <div className="flex h-24 w-full items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Cohorts</CardTitle>
            <CardDescription>Cohorts are evaluated in priority order — higher priority cohorts take precedence</CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Cohort
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Cohort</DialogTitle>
                <DialogDescription>Cohorts are created disabled with empty SQL by default.</DialogDescription>
              </DialogHeader>
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <strong>Engineering Approval Required:</strong> After creating this cohort, an engineering
                team member must write and test the SQL logic before the cohort can be activated. This
                prevents data integrity issues from invalid SQL.
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newCohortName}
                    onChange={(e) => setNewCohortName(e.target.value)}
                    placeholder="e.g., New Users"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newCohortDesc}
                    onChange={(e) => setNewCohortDesc(e.target.value)}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={!newCohortName || createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Priority</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24">Steps</TableHead>
              <TableHead className="w-40">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cohorts?.map((cohort, index) => (
              <TableRow key={cohort.id}>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span className="w-5 text-center text-sm font-medium text-muted-foreground">{index + 1}</span>
                    <div className="flex flex-col">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        aria-label={`Increase ${cohort.name} priority`}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === (cohorts?.length ?? 0) - 1}
                        aria-label={`Decrease ${cohort.name} priority`}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {cohort.name}
                    {cohort.twoWaySms && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <MessageSquare className="h-3 w-3" />
                        2-Way
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{cohort.description}</TableCell>
                <TableCell>
                  {cohort.locked ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
                            <Lock className="h-3 w-3" />
                            Locked
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          A developer must add SQL and unlock this cohort before it can be enabled
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Switch
                      checked={cohort.enabled}
                      onCheckedChange={() => handleToggleEnabled(cohort)}
                      disabled={updateMutation.isPending && updateMutation.variables?.id === cohort.id}
                      aria-label={`Toggle ${cohort.name} enabled`}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{cohort.stepCount}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(cohort)}
                      aria-label={`Edit cohort ${cohort.name}`}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    {cohort.locked && isDeveloper && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unlockMutation.mutate(cohort.id)}
                        disabled={unlockMutation.isPending}
                        aria-label={`Unlock cohort ${cohort.name}`}
                      >
                        {unlockMutation.isPending && unlockMutation.variables === cohort.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Unlock className="h-3 w-3 text-amber-600" />
                        )}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onSelectCohort(cohort.id)}>
                      Steps
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(cohort.id)}
                      disabled={deleteMutation.isPending && deletingCohortId === cohort.id}
                      aria-label={`Delete cohort ${cohort.name}`}
                    >
                      {deleteMutation.isPending && deletingCohortId === cohort.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3 text-destructive" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ConfirmActionDialog
          isOpen={!!deletingCohortId}
          onClose={() => setDeletingCohortId(null)}
          onConfirm={handleConfirmDelete}
          loading={deleteMutation.isPending}
          title="Delete Cohort"
          description="Delete this cohort? All steps must be deleted first. This action cannot be undone."
          confirmText="Delete"
        />

        {editingCohort && (
          <Dialog open={!!editingCohort} onOpenChange={() => setEditingCohort(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Cohort</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="two-way-sms">2-Way SMS</Label>
                    <p id="two-way-sms-desc" className="text-xs text-muted-foreground">Enable two-way SMS conversations for this cohort</p>
                  </div>
                  <Switch
                    id="two-way-sms"
                    aria-describedby="two-way-sms-desc"
                    checked={editTwoWaySms}
                    onCheckedChange={setEditTwoWaySms}
                  />
                </div>
                <div>
                  <Label>SQL Statement</Label>
                  {!isDeveloper && (
                    <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
                      Only developers can edit SQL. Contact engineering to update.
                    </p>
                  )}
                  <Textarea
                    value={editSql}
                    onChange={(e) => setEditSql(e.target.value)}
                    placeholder="SELECT id FROM users WHERE ..."
                    className="font-mono text-sm"
                    rows={6}
                    disabled={!isDeveloper}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingCohort(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
};